import AbstractCustomerValueModel from '../abstract_customer_value_model';
import BenefitsChartValue from '../../charts/benefits_chart_value';
import BenefitsWaterfallChart from '../../charts/benefits_waterfall_chart';

class RadarFirstRoi extends AbstractCustomerValueModel {
  custom_chart_classes() {
    return { BenefitsChartValue, BenefitsWaterfallChart };
  }

  // IMPACT VARIABLES ==================

  percent_reduction_in_time_on_incident_intake() {
    return this.input.percent_reduction_in_time_on_incident_intake / 100;
  }

  percent_reduction_in_incidents_requiring_follow_up() {
    return this.percent_reduction_in_time_on_incident_intake(); // simplification for web
  }

  percent_reduction_in_time_to_assess_incidents() {
    return this.input.percent_reduction_in_time_to_assess_incidents / 100;
  }

  percent_reduction_in_time_on_reporting() {
    return this.input.percent_reduction_in_time_on_reporting / 100;
  }

  percent_reduction_in_incidents_requiring_legal_review() {
    return this.input.percent_reduction_in_incidents_requiring_legal_review / 100;
  }

  percent_reduction_in_spending_on_outside_legal_counsel() {
    return this.input.percent_reduction_in_spending_on_outside_legal_counsel / 100;
  }

  reduction_in_number_of_fines() {
    return this.input.reduction_in_number_of_fines;
  }

  percent_increase_in_annual_revenue() {
    return this.input.percent_increase_in_annual_revenue / 100;
  }

  percent_increase_in_annual_revenue_attr() {
    return this.input.percent_increase_in_annual_revenue_attr / 100;
  }

  // USER INPUTS =======================

  // K12
  incidents_per_month() {
    return this.input.incidents_per_month;
  }

  // K21
  minutes_on_incident_intake() {
    return this.input.minutes_on_incident_intake;
  }

  // K26
  hours_to_assess_each_incident() {
    return this.input.hours_to_assess_each_incident;
  }

  // K29
  hours_on_reporting_per_month() {
    return this.input.hours_on_reporting_per_month;
  }

  // K32
  percent_of_incidents_per_month_requiring_legal_review() {
    return this.input.percent_of_incidents_per_month_requiring_legal_review / 100;
  }

  // K35
  annual_cost_of_outside_legal_counsel() {
    return this.input.annual_cost_of_outside_legal_counsel;
  }

  // K15
  annual_revenue() {
    return this.input.annual_revenue;
  }

  // K40 + K41
  cost_of_fines_and_investigations() {
    return this.input.cost_of_fines_and_investigations;
  }

  // Configurator - AB51
  annual_subscription_cost() {
    return this.input.annual_subscription_cost;
  }

  // Configurator - AB53
  other_annual_costs() {
    return this.input.other_annual_costs;
  }

  // Configurator - AB55
  other_one_time_costs() {
    return this.input.other_one_time_costs;
  }

  // K24
  staff_salary_for_incident_intake_follow_up() {
    return this.input.staff_salary_for_incident_intake_follow_up;
  }

  // K27
  staff_salary_for_incident_assessment() {
    return this.input.staff_salary_for_incident_assessment;
  }

  // K30
  staff_salary_for_reporting() {
    return this.input.staff_salary_for_reporting;
  }

  // K34
  staff_salary_for_legal_review() {
    return this.input.staff_salary_for_legal_review;
  }

  // DERIVED INPUTS ====================

  fully_burdened_salary(salary) {
    return salary * 1.3;
  }

  // STATIC INPUTS =====================

  // K13
  percent_increase_in_incidents_per_year() {
    return 0.05;
  }

  // K22
  percent_of_incidents_that_require_follow_up() {
    return 0.75;
  }

  // K23
  minutes_on_following_up_per_incident() {
    return 30;
  }

  // Configurator - K73
  days_worked_per_fte_per_year() {
    return 236;
  }

  // Configurator - K77
  hours_worked_per_fte_per_day() {
    return 7;
  }

  // K33
  hours_required_for_legal_review_per_incident() {
    return 2;
  }

  // K39
  fines_per_year() {
    return 2;
  }

  // VALUE DRIVER LABELS ===============

  save_time_and_cost_label() {
    return this.input.save_time_and_cost_label;
  }

  reduce_risk_label() {
    return this.input.reduce_risk_label;
  }

  build_trust_label() {
    return this.input.build_trust_label;
  }

  // BENEFIT LABELS ====================

  reduce_time_spent_on_incident_intake_label() {
    return this.input.reduce_time_spent_on_incident_intake_label;
  }

  reduce_time_spent_on_incident_assessment_label() {
    return this.input.reduce_time_spent_on_incident_assessment_label;
  }

  reduce_time_spent_generating_reports_label() {
    return this.input.reduce_time_spent_generating_reports_label;
  }

  reduce_internal_legal_time_required_label() {
    return this.input.reduce_internal_legal_time_required_label;
  }

  reduce_spending_on_outside_counsel_label() {
    return this.input.reduce_spending_on_outside_counsel_label;
  }

  reduce_the_risk_of_investigations_and_fines_label() {
    return this.input.reduce_the_risk_of_investigations_and_fines_label;
  }

  reduce_risk_of_reputational_damage_label() {
    return this.input.reduce_risk_of_reputational_damage_label;
  }

  // BENEFIT CALCULATIONS ==============

  all_benefits() {
    return [
      'reduce_time_spent_on_incident_intake',
      'reduce_time_spent_on_incident_assessment',
      'reduce_time_spent_generating_reports',
      'reduce_internal_legal_time_required',
      'reduce_spending_on_outside_counsel',
      'reduce_the_risk_of_investigations_and_fines',
      'reduce_risk_of_reputational_damage'
    ];
  }

  value_drivers() {
    return ['save_time_and_cost', 'reduce_risk', 'build_trust'];
  }

  save_time_and_cost(year) {
    return this.reduce_time_spent_on_incident_intake(year) + this.reduce_time_spent_on_incident_assessment(year)
      + this.reduce_time_spent_generating_reports() + this.reduce_internal_legal_time_required(year)
      + this.reduce_spending_on_outside_counsel();
  }

  reduce_risk() {
    return this.reduce_the_risk_of_investigations_and_fines();
  }

  build_trust() {
    return this.reduce_risk_of_reputational_damage();
  }

  reduce_time_spent_on_incident_intake(year = 1) {
    const intake_hours = (this.incidents_per_month() * 12) * (this.minutes_on_incident_intake() / 60);
    const incidents_for_follow_up = this.incidents_per_month() * this.percent_of_incidents_that_require_follow_up();
    const follow_up_cost = (incidents_for_follow_up * 12) * (this.minutes_on_following_up_per_incident() / 60);
    const staff_cost = this.fully_burdened_salary(this.staff_salary_for_incident_intake_follow_up())
      / this.days_worked_per_fte_per_year() / this.hours_worked_per_fte_per_day();
    const benefit_amount = (
      (intake_hours * this.percent_reduction_in_time_on_incident_intake())
        + (follow_up_cost * this.percent_reduction_in_incidents_requiring_follow_up())
    ) * staff_cost;
    return this.cumulative_benefit_multiplier(year, benefit_amount);
  }

  reduce_time_spent_on_incident_assessment(year = 1) {
    const assessment_hours = (this.incidents_per_month() * 12) * (this.hours_to_assess_each_incident());
    const staff_cost = this.fully_burdened_salary(this.staff_salary_for_incident_assessment())
      / this.days_worked_per_fte_per_year() / this.hours_worked_per_fte_per_day();
    const benefit_amount = (assessment_hours * this.percent_reduction_in_time_to_assess_incidents()) * staff_cost;
    return this.cumulative_benefit_multiplier(year, benefit_amount);
  }

  reduce_time_spent_generating_reports() {
    const reports_hours = this.hours_on_reporting_per_month() * 12;
    const staff_cost = this.fully_burdened_salary(this.staff_salary_for_reporting())
      / this.days_worked_per_fte_per_year() / this.hours_worked_per_fte_per_day();
    return (reports_hours * this.percent_reduction_in_time_on_reporting()) * staff_cost;
  }

  reduce_internal_legal_time_required(year = 1) {
    const incidents_for_review = this.percent_of_incidents_per_month_requiring_legal_review()
      * this.incidents_per_month();
    const review_hours = incidents_for_review * this.hours_required_for_legal_review_per_incident() * 12;
    const staff_cost = this.fully_burdened_salary(this.staff_salary_for_legal_review())
      / this.days_worked_per_fte_per_year() / this.hours_worked_per_fte_per_day();
    const benefit_amount = (review_hours * this.percent_reduction_in_incidents_requiring_legal_review()) * staff_cost;
    return this.cumulative_benefit_multiplier(year, benefit_amount);
  }

  reduce_spending_on_outside_counsel() {
    return this.annual_cost_of_outside_legal_counsel() * this.percent_reduction_in_spending_on_outside_legal_counsel();
  }

  reduce_the_risk_of_investigations_and_fines() {
    const pre_cost = this.fines_per_year() * this.cost_of_fines_and_investigations();
    const reduced_fines_per_year = Math.max(0, this.fines_per_year() - this.reduction_in_number_of_fines());
    const post_cost = reduced_fines_per_year * this.cost_of_fines_and_investigations();
    return pre_cost - post_cost;
  }

  reduce_risk_of_reputational_damage() {
    const due_to_radar = this.percent_increase_in_annual_revenue_attr();
    return this.annual_revenue() * this.percent_increase_in_annual_revenue() * due_to_radar;
  }

  cumulative_benefit_multiplier(year, benefit_amount) {
    if (year === 1) return benefit_amount;
    return benefit_amount * ((1 + this.percent_increase_in_incidents_per_year()) ** (year - 1));
  }

  adoption_rate(year) {
    switch (year) {
      case 1: return 0.8;
      case 2: return 0.95;
      default: return 1.0;
    }
  }

  // override parent
  cumulative_value_for_benefit(benefit, end_year) {
    const incident_benefits = [
      'save_time_and_cost',
      'reduce_time_spent_on_incident_intake',
      'reduce_time_spent_on_incident_assessment',
      'reduce_internal_legal_time_required'
    ];

    if (incident_benefits.includes(benefit)) {
      return (this.years_range(end_year).map((year) => this.adoption_rate(year) * this[benefit](year))).reduce(
        (x, y) => x + y, 0
      );
    }

    return super.cumulative_value_for_benefit(benefit, end_year);
  }

  // INVESTMENT CALCULATIONS ===========

  total_annual_investment(year) {
    const base_cost = this.annual_subscription_cost() + this.other_annual_costs();
    if (year === 1) {
      return base_cost + this.onboarding_costs() + this.other_one_time_costs();
    }
    return base_cost;
  }

  // Configurator - AB54
  onboarding_costs() {
    if (this.input.onboarding_costs_edited === 'true') {
      return this.input.onboarding_costs;
    }
    return this.calculated_onboarding_costs();
  }

  calculated_onboarding_costs() {
    return this.annual_subscription_cost() * 0.2;
  }
}

export default RadarFirstRoi;
